import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo, useState } from 'react';

import { gengql } from '@/__generated__';
import Link from '@/components/Link';
import Dropdown from '@/componentsv2/Dropdown';
import { getInitials } from '@/utils/getInitials';
import { stringToColor } from '@/utils/stringToColor';

import { DashboardCard } from './DashboardCard';

const EXPERT_CANDIDACIES = gengql(/* GraphQL */ `
  query dashboardExpertCandidacies($pageSize: Int!) {
    searchConsultations(
      states: [negotiating_expert_time, negotiating_client_time, awaiting_expert_review, confirmed]
      first: $pageSize
      user_context: "expert"
      sort: "created_at"
      sort_order: "desc"
    ) {
      edges {
        node {
          id
          html_url
          state
          starts_at
          expert_request {
            id
            name
          }
          requester {
            id
            name
            first_name
          }
          requester_name
        }
      }
    }
  }
`);

interface StateConfig {
  label: string;
  style: string;
}

const STATE_CONFIG: Record<string, StateConfig> = {
  negotiating_expert_time: {
    label: 'Awaiting your response',
    style: 'bg-yellow-500/15 text-yellow-800 dark:text-yellow-300',
  },
  negotiating_client_time: {
    label: 'Waiting on client',
    style: 'bg-teal-500/15 text-teal-800 dark:text-teal-300',
  },
  confirmed: {
    label: 'Confirmed',
    style: 'bg-green-500/15 text-green-700 dark:text-green-300',
  },
  awaiting_expert_review: {
    label: 'Written response due',
    style: 'bg-surface-secondary text-on-surface-secondary',
  },
};

type CandidacySort = 'date' | 'state' | 'client';

const SORT_OPTIONS: Array<{ value: CandidacySort; display: string }> = [
  { value: 'date', display: 'Most recent' },
  { value: 'state', display: 'Status' },
  { value: 'client', display: 'Client name' },
];

const STATE_ORDER: Record<string, number> = {
  negotiating_expert_time: 0,
  awaiting_expert_review: 1,
  negotiating_client_time: 2,
  confirmed: 3,
};

export function MyCandidaciesCard(): React.ReactNode {
  const { data, loading, error } = useQuery(EXPERT_CANDIDACIES, {
    variables: { pageSize: 20 },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (error) Sentry.captureException(error);
  }, [error]);

  const consultations = useMemo(
    () => data?.searchConsultations?.edges?.map((e) => e?.node).filter(Boolean) ?? [],
    [data]
  );

  const [sort, setSort] = useState<CandidacySort>('date');

  const sorted = useMemo(() => {
    return [...consultations].sort((a, b) => {
      switch (sort) {
        case 'date':
          return (b.starts_at ?? b.id ?? '').localeCompare(a.starts_at ?? a.id ?? '');
        case 'state':
          return (STATE_ORDER[a.state ?? ''] ?? 99) - (STATE_ORDER[b.state ?? ''] ?? 99);
        case 'client': {
          const nameA = a.requester?.name ?? a.requester_name ?? '';
          const nameB = b.requester?.name ?? b.requester_name ?? '';
          return nameA.localeCompare(nameB);
        }
      }
    });
  }, [consultations, sort]);

  const count = consultations.length;

  return (
    <DashboardCard
      title="My Engagements"
      badge={count}
      headerRight={
        count > 0 ? (
          <Dropdown
            id="candidacies-sort"
            options={SORT_OPTIONS}
            value={sort}
            onChange={(v) => setSort(v as CandidacySort)}
          />
        ) : undefined
      }
      linkText="View all"
      linkTo="/consultations"
      loading={loading && count === 0}
      error={!!error && count === 0}
      fullWidth
    >
      {sorted.length === 0 ? (
        <p className="text-12 text-on-surface-muted">No active engagements right now.</p>
      ) : (
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3">
          {sorted.map((c) => {
            const state = STATE_CONFIG[c.state ?? ''];
            const clientName =
              c.requester?.name ?? c.requester?.first_name ?? c.requester_name ?? 'Client';
            const requestName = c.expert_request?.name;
            const initials = getInitials(c.requester?.first_name, null);
            const bgColor = stringToColor(clientName, 700);

            return (
              <Link
                key={c.id}
                to={c.html_url ?? '#'}
                className="flex items-start gap-10 rounded-md border border-surface-border p-12 no-underline transition-colors hover:bg-surface-elevated"
              >
                <div
                  className="flex h-[3.2rem] w-[3.2rem] shrink-0 items-center justify-center rounded-full text-12 font-semibold text-white"
                  style={{ backgroundColor: bgColor }}
                  aria-hidden="true"
                >
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="m-0 truncate text-14 font-medium text-on-surface">
                    {requestName ?? clientName}
                  </p>
                  <p className="m-0 truncate text-12 text-on-surface-muted">{clientName}</p>
                  {state && (
                    <span
                      className={`mt-4 inline-flex items-center rounded px-6 py-1 text-[1.1rem] font-medium ${state.style}`}
                    >
                      {state.label}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </DashboardCard>
  );
}
